import { useCallback, useEffect, useRef, useState } from 'react'

type ResetKeys = {
  filterExecutionTime: Date | undefined
  uuid_classification: string
  objectclassUuid: string
}

/**
 * Reserves max observed height around the section
 * transition so shorter sections do not shrink the
 * document and pull scroll position upward.
 */
const useCockpitSectionTransitionMinHeight = ({
  filterExecutionTime,
  uuid_classification,
  objectclassUuid
}: ResetKeys): {
  transitionRef: React.RefObject<HTMLDivElement>
  animationWrapperStyle: React.CSSProperties | undefined
  reserveHeightBeforeSectionChange: () => void
  reserveHeightOnTransitionEntered: () => void
} => {
  const transitionRef = useRef<HTMLDivElement>(null)
  const [reservedMinHeight, setReservedMinHeight] = useState(0)

  useEffect(() => {
    setReservedMinHeight(0)
  }, [filterExecutionTime, uuid_classification, objectclassUuid])

  const reserveHeightFromMeasuredNode = useCallback(() => {
    const measuredHeight =
      transitionRef.current?.getBoundingClientRect().height ?? 0
    setReservedMinHeight((prev) => Math.max(prev, measuredHeight))
  }, [])

  const syncHeightToMeasuredNode = useCallback(() => {
    const measuredHeight =
      transitionRef.current?.getBoundingClientRect().height ?? 0
    setReservedMinHeight(measuredHeight)
  }, [])

  const animationWrapperStyle = reservedMinHeight
    ? { minHeight: reservedMinHeight }
    : undefined

  return {
    transitionRef,
    animationWrapperStyle,
    reserveHeightBeforeSectionChange: reserveHeightFromMeasuredNode,
    reserveHeightOnTransitionEntered: syncHeightToMeasuredNode
  }
}

export default useCockpitSectionTransitionMinHeight
